import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { BodySection, ActionSection } from 'components/UI/Section';
import Section from '../Section';
import ServiceTypeSelector from './UI/ServiceTypeSelector';
import PricingTable from './PricingTable';
import { usePricingContext } from './PricingContext/PricingContext';
import Service, { ServicePricing } from 'model/Service';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { buildPricingTaxesFromMarketplaces, buildSelectedTaxElements, buildTaxElements } from './utils/taxElements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import clsx from 'clsx';

interface PricingProps {
    disabled?: boolean;
    collapsed?: boolean;
    editable?: boolean;
    completed?: boolean;
    modifiable?: boolean;
    onEdit?: () => void;
    onCollapse?: () => void;
    onChange?: (servicePricingObject: ServicePricing) => void;
    onChangeMarketplaces?: (marketplaces: Service['marketplaces']) => void;
    onNext?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        titleDescriptionSelectorContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
        },
        titleDescriptionContainer: {
            display: 'flex',
            flexDirection: 'column'
        },
        titleContainer: {
            marginBottom: 10
        },
        title: {
            'fontSize': 20,
            'fontWeight': 600,
            '& span': {
                fontWeight: 600,
                color: '#EAB464'
            }
        },
        descriptionContainer: {
            marginBottom: 25
        },
        description: {
            fontSize: 14,
            fontWeight: 400,
            color: '#000',

            [theme.breakpoints.up('md')]: {
                fontSize: 18
            }
        },
        selectorContainer: {
            display: 'flex'
        },
        priceContainer: {
            display: 'flex',
            [theme.breakpoints.down('lg')]: {
                marginTop: '25px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '33px'
            }
        },
        taxable: {
            '& .MuiCheckbox-root': {
                paddingLeft: '0px !important'
            },
            '& p': {
                [theme.breakpoints.down('lg')]: {
                    fontSize: '12px'
                },
                [theme.breakpoints.up('xl')]: {
                    fontSize: '16px'
                }
            }
        },
        errorMessage: {
            color: 'red',
            marginTop: 10,
            [theme.breakpoints.down('sm')]: {
                fontSize: 14
            }
        },
        taxesSelectorDisabled: {
            opacity: 0.5,
            pointerEvents: 'none'
        },
        taxesContainer: {
            marginBottom: 20
        },
        taxesSectionTitle: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            margin: 0,

            [theme.breakpoints.up('xl')]: {
                fontSize: 20
            }
        },
        marketplaceTaxesSection: {
            marginTop: 24,

            [theme.breakpoints.up('xl')]: {
                marginTop: 30
            }
        },
        marketplaceHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            cursor: 'pointer',
            userSelect: 'none',
            paddingBottom: 12
        },
        marketplaceHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0
        },
        marketplaceName: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            margin: 0,

            [theme.breakpoints.up('xl')]: {
                fontSize: 20
            }
        },
        marketplaceCount: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            margin: 0,
            whiteSpace: 'nowrap',

            [theme.breakpoints.up('xl')]: {
                fontSize: 20
            }
        },
        marketplaceChevron: {
            fontSize: 14,
            color: '#000',
            flexShrink: 0,

            [theme.breakpoints.up('xl')]: {
                fontSize: 16
            }
        },
        marketplaceNameDivider: {
            width: '100%',
            height: 1,
            backgroundColor: '#000'
        },
        marketplaceTaxesContent: {
            marginTop: 22,

            [theme.breakpoints.up('xl')]: {
                marginTop: 30
            }
        },
        taxesNotDefined: {
            fontSize: 14,
            fontWeight: 400,
            color: '#7A7A7A',
            margin: 0,

            [theme.breakpoints.up('md')]: {
                fontSize: 16
            }
        }
    })
);

const Pricing: FunctionComponent<PricingProps> = props => {
    const classes = useStyles();
    const { organizationServicesInfo } = useOrganizationMarketplaces();

    const { updateTaxes, createServicePricingObject, service } = usePricingContext();
    const { pricing } = usePricingContext().state;

    const [serviceDone, setServiceDone] = useState(false);
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);

    const isNewService = service.id === undefined;

    const hasPositivePrice = useMemo(
        () =>
            Array.isArray(pricing)
                ? pricing.some(
                      price =>
                          (price as { price?: number }).price !== undefined && (price as { price?: number }).price! > 0
                  )
                : false,
        [pricing]
    );

    const selectedMarketplaces = useMemo(
        () =>
            service.marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: (typeof organizationServicesInfo)[number];
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, service.marketplaces]
    );

    useEffect(() => {
        if (!isNewService || hasPositivePrice || !props.onChangeMarketplaces) {
            return;
        }

        if (service.marketplaces.some(marketplace => marketplace.taxes.length > 0)) {
            props.onChangeMarketplaces(
                service.marketplaces.map(marketplace => ({
                    ...marketplace,
                    taxes: []
                }))
            );
            updateTaxes([]);
        }
    }, [hasPositivePrice, isNewService, props.onChangeMarketplaces, service.marketplaces, updateTaxes]);

    const collapseHandler = () => {
        props.onCollapse && props.onCollapse();
    };

    const editHandler = () => {
        if (!props.disabled) {
            if (props.collapsed && (props.editable || props.modifiable)) {
                props.onEdit && props.onEdit();
            } else {
                props.onCollapse && props.onCollapse();
            }
        }
    };

    const marketplaceTaxSelectionChangeHandler =
        (marketplaceId: number) =>
        (elements: Array<Element<number>>): void => {
            if (!props.onChangeMarketplaces) {
                return;
            }

            const updatedMarketplaces = service.marketplaces.map(marketplace =>
                marketplace.marketplaceId === marketplaceId
                    ? { ...marketplace, taxes: elements.map(element => element.id) }
                    : marketplace
            );

            props.onChangeMarketplaces(updatedMarketplaces);
            updateTaxes(buildPricingTaxesFromMarketplaces(updatedMarketplaces, organizationServicesInfo));
        };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    const finishHandler = () => {
        createServicePricingObject();
        props.onNext && props.onNext();
    };

    return (
        <Section
            position={3}
            title="Pricing"
            onEdit={props.disabled ? undefined : editHandler}
            onCollapse={props.disabled ? undefined : collapseHandler}
            disabled={props.disabled}
            collapsed={props.collapsed}
            modifiable={props.modifiable}
            editable={props.editable}
            completed={props.completed}
        >
            <BodySection>
                <Box className={classes.container}>
                    <Box className={classes.titleDescriptionContainer}>
                        <Box className={classes.titleContainer}>
                            <Typography className={classes.title}>
                                Service <span>*</span>
                            </Typography>
                            <Box className={classes.descriptionContainer}>
                                <Typography className={classes.description}>
                                    Select whether your service has fixed or variable pricing. Variable pricing allows
                                    you to set prices based on pet size, and/or hair length.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <ServiceTypeSelector />
                    <PricingTable onServiceDone={setServiceDone} />
                    <Box>
                        <Box
                            className={clsx(classes.taxesContainer, {
                                [classes.taxesSelectorDisabled]: !hasPositivePrice
                            })}
                        >
                            <Typography className={classes.taxesSectionTitle}>
                                Choose the applicable tax for each location
                            </Typography>
                            {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                                const availableTaxIds = new Set(servicesInfo.taxes.map(tax => tax.id));
                                const selectedCount = serviceMarketplace.taxes.filter(taxId =>
                                    availableTaxIds.has(taxId)
                                ).length;
                                const totalCount = servicesInfo.taxes.length;
                                const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                                return (
                                    <Box
                                        key={serviceMarketplace.marketplaceId}
                                        className={classes.marketplaceTaxesSection}
                                    >
                                        <Box
                                            className={classes.marketplaceHeader}
                                            onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                                        >
                                            <Box className={classes.marketplaceHeaderLeft}>
                                                <Typography className={classes.marketplaceName}>
                                                    {servicesInfo.marketplace.name}
                                                </Typography>
                                                <Typography className={classes.marketplaceCount}>
                                                    ({selectedCount}/{totalCount})
                                                </Typography>
                                            </Box>
                                            <FontAwesomeIcon
                                                icon={isExpanded ? faChevronUp : faChevronDown}
                                                className={classes.marketplaceChevron}
                                            />
                                        </Box>
                                        <Box className={classes.marketplaceNameDivider} />
                                        {isExpanded && (
                                            <Box className={classes.marketplaceTaxesContent}>
                                                {servicesInfo.taxes.length > 0 ? (
                                                    <ChipSelector
                                                        id={`services_add_service_pricing_taxes_selector_${serviceMarketplace.marketplaceId}`}
                                                        searchLabel="Search taxes"
                                                        elements={buildTaxElements(servicesInfo.taxes)}
                                                        selectedElements={buildSelectedTaxElements(
                                                            servicesInfo.taxes,
                                                            serviceMarketplace.taxes
                                                        )}
                                                        changeSelection={marketplaceTaxSelectionChangeHandler(
                                                            serviceMarketplace.marketplaceId
                                                        )}
                                                    />
                                                ) : (
                                                    <Typography className={classes.taxesNotDefined}>
                                                        Taxes not defined
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </BodySection>
            <ActionSection
                id="services_add_service_pricing_button_next"
                onNext={finishHandler}
                disabled={!serviceDone}
            />
        </Section>
    );
};

export default Pricing;
